import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { ChevronDown, Home, ChevronLeft, ChevronRight, Settings, User as UserIcon, Check, Plus, HelpCircle, Bell, Search } from "react-feather";
import { all_routes } from "../../Router/all_routes";
import axios from "axios";
import { base_api_url } from "../../environment.jsx";

const ModernHeader = ({ onToggleSidebar, isCollapsed }) => {
  const navigate = useNavigate();
  const [prenom, setPrenom] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isRestoDropdownOpen, setIsRestoDropdownOpen] = useState(false);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedResto, setSelectedResto] = useState(null);
  const [restoSearchQuery, setRestoSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const restoDropdownRef = useRef(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setPrenom(userData.prenom || userData.nom || "Utilisateur");
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (restoDropdownRef.current && !restoDropdownRef.current.contains(event.target)) {
        setIsRestoDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchRestaurants = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const token = user?.access_token;
    if (!token) return;
    try {
      const response = await axios.get(`${base_api_url}api/restaurant`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200 || response.status === 201) {
        const data = response.data.restaurant || [];
        setRestaurants(data);

        const currentRestoId = user?.restoId;
        const current = data.find(r => r.id_restaurant === currentRestoId);
        setSelectedResto(current);
      }
    } catch (error) {
      console.error("Error fetching restaurants for header:", error);
    }
  };

  useEffect(() => {
    fetchRestaurants();
    window.addEventListener("restaurantChanged", fetchRestaurants);
    return () => window.removeEventListener("restaurantChanged", fetchRestaurants);
  }, []);

  const handleSelectResto = async (resto) => {
    localStorage.setItem("selectedRestaurantId", resto.id_restaurant.toString());
    try {
      const gotResto = await axios.get(`${base_api_url}api/restaurant/${resto.id_restaurant}`);
      localStorage.setItem("restaurant", JSON.stringify(gotResto.data));

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      user.restoId = resto.id_restaurant;
      localStorage.setItem("user", JSON.stringify(user));

      setSelectedResto(resto);
      setIsRestoDropdownOpen(false);
      window.dispatchEvent(new Event("restaurantChanged"));
    } catch (err) {
      console.error("Erreur lors de la sélection :", err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("restaurant");
    window.location.href = all_routes.signin;
  };

  const handleCreateRestaurant = () => {
    setIsRestoDropdownOpen(false);
    navigate(all_routes.Restaurants + '?create=true');
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const names = name.split(" ");
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <>
      <header className={`pixel-header ${isCollapsed ? 'pixel-header--collapsed' : 'pixel-header--expanded'}`}>
        <div className="pixel-header__left">
          {/* Toggle Button */}
          <button
            onClick={onToggleSidebar}
            className="pixel-header__toggle"
            aria-label="Toggle Sidebar"
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>

          {/* Restaurant selector */}
          <div className="pixel-resto-selector" ref={restoDropdownRef}>
            <div
              className="pixel-resto-button"
              onClick={() => setIsRestoDropdownOpen(!isRestoDropdownOpen)}
            >
              <div className="pixel-resto-icon-wrapper">
                <Home size={18} />
              </div>
              <div className="pixel-resto-info">
                <span className="pixel-resto-name">
                  {selectedResto?.nom || "Choisir un Restaurant"}
                </span>
                {selectedResto?.ville && (
                  <>
                    <span className="pixel-resto-separator">—</span>
                    <span className="pixel-resto-location">
                      {selectedResto.ville}
                    </span>
                  </>
                )}
                <span className="pixel-resto-badge">EUR</span>
              </div>
              <ChevronDown className={`pixel-resto-chevron ${isRestoDropdownOpen ? 'open' : ''}`} size={14} />
            </div>

            {isRestoDropdownOpen && (
              <div className={`pixel-resto-dropdown shadow-lg visible`}>
                <div className="pixel-resto-search">
                  <Search size={14} className="pixel-resto-search-icon" />
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    className="pixel-resto-search-input"
                    value={restoSearchQuery}
                    onChange={(e) => setRestoSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                <div className="pixel-dropdown-divider" />

                <div className="restaurant-list-px">
                  {restaurants
                    .filter(r => r.nom.toLowerCase().includes(restoSearchQuery.toLowerCase()))
                    .map((resto) => (
                      <div
                        key={resto.id_restaurant}
                        className={`restaurant-item-px ${resto.id_restaurant === selectedResto?.id_restaurant ? 'active' : ''}`}
                        onClick={() => handleSelectResto(resto)}
                      >
                        <div className="item-icon-px-wrapper">
                          <Home size={16} />
                        </div>
                        <div className="item-details-px">
                          <span className="item-name-px">{resto.nom}</span>
                          <span className="item-meta-px">
                            {resto.ville ? `${resto.ville} · ` : ""}EUR
                          </span>
                        </div>
                        {resto.id_restaurant === selectedResto?.id_restaurant && (
                          <Check size={16} className="item-check-icon" />
                        )}
                      </div>
                    ))}
                </div>

                <div className="pixel-dropdown-divider" />

                <div className="pixel-resto-actions">
                  <button className="pixel-resto-action-btn" onClick={handleCreateRestaurant}>
                    <Plus size={16} />
                    <span>Créer un restaurant</span>
                  </button>
                  <button className="pixel-resto-action-btn" onClick={() => window.location.href = all_routes.Restaurants}>
                    <Settings size={16} />
                    <span>Gérer les restaurants</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Section - Paramètres, Aide, Bell, Avatar */}
        <div className="pixel-header__right">
          {/* Paramètres */}
          <Link to={all_routes.settings} className="pixel-header__action-link">
            <Settings size={17} strokeWidth={1.8} />
            <span>Paramètres</span>
          </Link>

          {/* Aide */}
          <a
            href="https://qrnyama.com/aide.html"
            target="_blank"
            rel="noopener noreferrer"
            className="pixel-header__action-link"
          >
            <HelpCircle size={17} strokeWidth={1.8} />
            <span>Aide</span>
          </a>

          {/* Notification Bell */}
          <button className="pixel-header__icon-btn" aria-label="Notifications">
            <Bell size={19} strokeWidth={1.8} />
          </button>

          {/* User Avatar */}
          <div className="pixel-user-selector" ref={dropdownRef}>
            <div onClick={toggleDropdown} className="pixel-user-trigger">
              <div className="pixel-avatar">
                {getInitials(prenom)}
              </div>
            </div>

            {/* Dropdown Menu */}
            <div className={`pixel-user-dropdown ${isDropdownOpen ? 'visible' : ''}`}>
              <Link
                to={all_routes.profile}
                className="pixel-dropdown-item"
                onClick={() => setIsDropdownOpen(false)}
              >
                <UserIcon size={16} className="me-2" />
                <span>Mon Profil</span>
              </Link>

              <Link
                to={all_routes.settings}
                className="pixel-dropdown-item"
                onClick={() => setIsDropdownOpen(false)}
              >
                <Settings size={16} className="me-2" />
                <span>Paramètres</span>
              </Link>

              <div className="pixel-dropdown-divider" />

              <button
                onClick={handleLogout}
                className="pixel-dropdown-item text-danger"
              >
                <UserIcon size={16} className="me-2" />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </header>

    </>
  );
};

ModernHeader.propTypes = {
  onToggleSidebar: PropTypes.func.isRequired,
  isCollapsed: PropTypes.bool.isRequired,
};

export default ModernHeader;
